<template>
  <b-modal
    v-model="modalVisible"
    id="task-details-modal"
    title="Task Details"
    size="lg"
    hide-footer
  >
    <div v-if="selectedTask">
      <!-- Task Overview -->
      <div class="mb-4">
        <b-row>
          <b-col md="6">
            <div class="mb-2">
              <strong>Task ID:</strong> {{ selectedTask.taskId }}
            </div>
            <div class="mb-2">
              <strong>Status:</strong>
              <b-badge
                :variant="getTaskStatusVariant(selectedTask.status)"
                class="ml-50"
              >
                {{ getTaskStatusText(selectedTask.status) }}
              </b-badge>
            </div>
          </b-col>
          <b-col md="6">
            <div class="mb-2">
              <strong>Created:</strong> {{ formatDateTime(selectedTask.createdAt) }}
            </div>
            <div
              v-if="selectedTask.completedAt"
              class="mb-2"
            >
              <strong>Completed:</strong> {{ formatDateTime(selectedTask.completedAt) }}
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Pre-Review Results -->
      <div
        v-if="selectedTask.results && selectedTask.results.length"
        class="mb-4"
      >
        <h5 class="mb-3">
          Pre-Review Results
        </h5>
        <div class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Filename / Creative ID</th>
                <th>Pre-Review Status</th>
                <th>Reject Reasons</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="result in selectedTask.results"
                :key="result.creative_id"
              >
                <td style="max-width: 250px; word-wrap: break-word;">
                  <div v-if="isTextBasedCreative(result) || isTextMaterialResult(result)">
                    <div class="mb-1">
                      <b-badge
                        variant="light-info"
                        size="sm"
                      >
                        {{ getMaterialTypeLabel(result) }}
                      </b-badge>
                    </div>
                    <div
                      class="text-muted"
                      style="word-break: break-all; font-size: 0.875rem;"
                    >
                      {{ getTextContent(result) }}
                    </div>
                  </div>
                  <span v-else>
                    <template v-if="result.filename || result.original_filename">
                      <div style="word-break: break-all;">{{ result.filename || result.original_filename }}</div>
                      <small class="text-muted">ID: {{ result.creative_id }}</small>
                    </template>
                    <template v-else>
                      {{ result.creative_id }}
                    </template>
                  </span>
                </td>
                <td>
                  <b-badge
                    :variant="getTaskStatusVariant(result.pre_review_status || result.status)"
                    size="sm"
                  >
                    <feather-icon
                      :icon="getTaskStatusIcon(result.pre_review_status || result.status)"
                      size="12"
                      class="mr-1"
                    />
                    {{ getTaskStatusText(result.pre_review_status || result.status) }}
                  </b-badge>
                </td>
                <td>
                  <div v-if="result.reject_info_list && result.reject_info_list.length">
                    <small
                      v-for="(reason, idx) in result.reject_info_list"
                      :key="idx"
                      class="text-danger d-block"
                    >
                      • {{ reason }}
                    </small>
                  </div>
                  <small
                    v-else
                    class="text-muted"
                  >-</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'TaskDetailsModal',
  components: {
    BModal,
    BRow,
    BCol,
    BBadge,
    FeatherIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedTask: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      modalVisible: false,
    }
  },
  watch: {
    show: {
      handler(newVal) {
        this.modalVisible = newVal
      },
      immediate: true,
    },
    modalVisible(newVal) {
      this.$emit('input', newVal)
    },
  },
  methods: {
    getTaskStatusText(status) {
      const statusMap = {
        pending: 'Pending',
        processing: 'In Progress',
        completed: 'Completed',
        approved: 'Approved',
        rejected: 'Rejected',
        failed: 'Failed',
        // Pre-review material statuses
        APPROVED: 'Approved',
        REJECTED: 'Rejected',
        UNSURE: 'Unsure',
        UNAVAILABLE: 'Unavailable',
        // Lowercase versions
        unsure: 'Unsure',
        unavailable: 'Unavailable',
      }
      return statusMap[status] || status
    },
    getTaskStatusVariant(status) {
      const variantMap = {
        pending: 'light-warning',
        processing: 'light-info',
        completed: 'light-success',
        approved: 'light-success',
        rejected: 'light-danger',
        failed: 'light-danger',
        // Pre-review material statuses
        APPROVED: 'light-success',
        REJECTED: 'light-danger',
        UNSURE: 'light-warning',
        UNAVAILABLE: 'light-secondary',
        // Lowercase versions
        unsure: 'light-warning',
        unavailable: 'light-secondary',
      }
      return variantMap[status] || 'light-secondary'
    },
    getTaskStatusIcon(status) {
      const iconMap = {
        pending: 'ClockIcon',
        processing: 'RefreshCwIcon',
        completed: 'CheckCircleIcon',
        approved: 'CheckCircleIcon',
        rejected: 'XCircleIcon',
        failed: 'AlertTriangleIcon',
        // Pre-review material statuses
        APPROVED: 'CheckCircleIcon',
        REJECTED: 'XCircleIcon',
        UNSURE: 'HelpCircleIcon',
        UNAVAILABLE: 'MinusCircleIcon',
        // Lowercase versions
        unsure: 'HelpCircleIcon',
        unavailable: 'MinusCircleIcon',
      }
      return iconMap[status] || 'HelpCircleIcon'
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },
    isVideoMaterial(material) {
      if (!material) return false

      // Check material type
      const materialType = material.material_type || material.type || ''
      if (materialType.toLowerCase().includes('video')) {
        return true
      }

      // Check file extension from URL or content
      const contentUrl = material.content || material.url || material.creative_id || ''
      const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv']
      return videoExtensions.some(ext => contentUrl.toLowerCase().includes(ext))
    },
    getCreativeThumbnailUrl(material) {
      if (!material) return ''

      // Only process IMAGE materials
      if (material.type !== 'IMAGE' && material.material_type !== 'IMAGE') {
        return ''
      }

      // If content is a direct URL (from TikTok CDN), use it directly
      if (material.content && (material.content.startsWith('http://') || material.content.startsWith('https://'))) {
        return material.content
      }

      // For numeric creative IDs, use the API endpoint
      const creativeId = material.creative_id
      if (creativeId && /^\d+$/.test(creativeId.toString())) {
        const apiUrl = `/api/creative/image/${creativeId}`
        return apiUrl
      }

      return ''
    },
    handleImageError(event) {
      // Replace with a simple placeholder instead of hiding
      const placeholderUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iOCIgZmlsbD0iI0Y4RjlGQSIgc3Ryb2tlPSIjRTJFOEYwIiBzdHJva2Utd2lkdGg9IjIiLz4KPGRhdGggZD0iTTE2IDQwTDI0IDMyTDMyIDQwTDQ4IDI0VjQ4SDE2VjQwWiIgZmlsbD0iIzlDQTNBRiIvPgo8Y2lyY2xlIGN4PSIyNCIgY3k9IjI0IiByPSI0IiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo='
      const { target } = event
      target.src = placeholderUrl
      target.alt = 'Image not available'
      target.title = 'Image temporarily unavailable (CDN restriction)'
    },
    isTextMaterial(material) {
      if (!material) return false
      const materialType = (material.material_type || material.type || '').toUpperCase()
      return materialType === 'AD_TEXT' || materialType === 'LANDING_PAGE_URL'
    },
    isTextBasedCreative(result) {
      // Check if the creative_id looks like a material placeholder (material_1, material_2, etc.)
      const creativeId = result.creative_id || ''
      return creativeId.toString().startsWith('material_')
    },
    isTextMaterialResult(result) {
      // Check if this is a text material based on type
      if (!result) return false
      const materialType = (result.material_type || result.type || '').toUpperCase()
      return materialType === 'AD_TEXT' || materialType === 'LANDING_PAGE_URL'
    },
    getMaterialTypeLabel(result) {
      const materialType = (result.material_type || result.type || '').toUpperCase()
      if (materialType === 'AD_TEXT') return 'Ad Text'
      if (materialType === 'LANDING_PAGE_URL') return 'Landing Page URL'
      if (result.creative_id && result.creative_id.toString().startsWith('material_')) {
        // Try to infer from creative_id pattern
        if (result.creative_id.includes('text')) return 'Ad Text'
        if (result.creative_id.includes('url')) return 'Landing Page URL'
      }
      return 'Text Material'
    },
    getTextContent(result) {
      // For text-based results, try to extract the actual content
      if (result.content && typeof result.content === 'string' && !result.content.startsWith('material_') && !result.content.startsWith('ad_text')) {
        return result.content
      }
      // Check for ad_text field
      if (result.ad_text) {
        return result.ad_text
      }
      // Check for url field
      if (result.url) {
        return result.url
      }
      // Check for text field
      if (result.text) {
        return result.text
      }
      // Fallback to creative_id if no content
      return result.creative_id || 'No content available'
    },
    getTextMaterialIcon(material) {
      const materialType = (material.material_type || material.type || '').toUpperCase()
      if (materialType === 'AD_TEXT') {
        return 'TypeIcon'
      }
      if (materialType === 'LANDING_PAGE_URL') {
        return 'LinkIcon'
      }
      return 'FileTextIcon'
    },
    getTextMaterialContent(material) {
      // For text materials, the content should contain the actual text/URL
      if (material.content && !material.content.startsWith('material_')) {
        return material.content
      }
      // Fallback to creative_id if content is not available or is a material ID
      if (material.creative_id && !material.creative_id.startsWith('material_')) {
        return material.creative_id
      }
      // If both are material IDs, return a shortened version
      return material.material_type === 'AD_TEXT' ? 'Ad Text' : 'Landing Page'
    },
    getTextMaterialPreview(material) {
      const content = this.getTextMaterialContent(material)
      const materialType = (material.material_type || material.type || '').toUpperCase()

      if (materialType === 'AD_TEXT') {
        // Truncate ad text for preview
        return content.length > 15 ? `${content.substring(0, 15)}...` : content
      }
      if (materialType === 'LANDING_PAGE_URL') {
        // Show domain for URLs
        try {
          const url = new URL(content)
          return url.hostname
        } catch {
          return content.length > 15 ? `${content.substring(0, 15)}...` : content
        }
      }
      return content.length > 15 ? `${content.substring(0, 15)}...` : content
    },
  },
}
</script>

<style scoped>
.creative-thumbnail-small {
  width: 80px;
  height: 80px;
  display: inline-block;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.creative-thumbnail-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

/* Ensure table doesn't create horizontal scroll */
.table {
  table-layout: auto;
}

.table td {
  word-wrap: break-word;
  overflow-wrap: break-word;
}
</style>
